import { Button, Popover, Divider, Spin, Tag } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import moment from 'moment'
import React, { useState } from 'react'
import { ApiRequest } from '../../../../services/api/api'

interface IProps {
  id: number;
  label?: string;
  isNotCutting?: boolean;
}

const ItipsForOrderDetails: React.FC<IProps> = (props) => {
  const [visible, setVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const [getWorkOrderData, setGetWorkOrderData] = useState<any>({})
  const [getDispatchingData, setGetDispatchingData] = useState<any>({})
  const [getInvoiceData, setGetInvoiceData] = useState<any>({})
  const [getOrderData, setGetOrderData] = useState<any>({})  

  const handleVisibleChange = (visible: boolean) => {
    setVisible(visible)
    
    if (visible && props.id) {
      setLoading(true)
      
      Promise.all([
        ApiRequest({
          url: 'Dispatching/GetByOrderId?orderId=' + props.id,
          method: 'get',
          isShowSpinner: false,
        }),
        ApiRequest({
          url: 'Invoice/GetAllInvoice?orderId=' + props.id,
          method: 'get',
          isShowSpinner: false,
        }),
        ApiRequest({
          url: 'WorkOrder/GetAllWorkOrder?orderId=' + props.id,
          method: 'get',
          isShowSpinner: false,
        }),
        ApiRequest({
          url: 'SalesOrder/GetOrderById?id=' + props.id,
          method: 'get',
          isShowSpinner: false,
        })
      ]).then(([dispatch, invoice, workOrder, order]) => {
        setGetDispatchingData(dispatch.data.data)
        setGetInvoiceData(invoice.data.data)
        setGetWorkOrderData(workOrder.data.data)
        setGetOrderData(order.data.data)
      }).finally(() => {
        setLoading(false)
      })
    }
  }

  const getRowRender = (name: string, data?: string, warningData?: string, successData?: string) => (
    <div style={{
      wordWrap: 'break-word', 
      display: 'flex', 
      marginBottom: '8px',
      lineHeight: '1.6'
    }}>
      <div style={{ 
        minWidth: '120px',
        color: '#595959',
        fontWeight: 500
      }}>
        {name}:
      </div>
      <div style={{ flex: 1 }}>
        <span style={{ wordBreak: 'break-all', color: '#262626' }}>{data}</span>
        {warningData && (
          <span style={{ 
            wordBreak: 'break-all', 
            color: '#ff4d4f',
            fontWeight: 600,
            marginLeft: '8px'
          }}>
            {warningData}
          </span>
        )}
        {successData && (
          <span style={{ 
            wordBreak: 'break-all', 
            color: '#52c41a',
            fontWeight: 600,
            marginLeft: '8px'
          }}>
            {successData}
          </span>
        )}
      </div>
    </div>
  )

  const renderPackingSlipProducts = (productString: string) => {
    if (!productString) return null
    
    const products = productString.split(', ')
    
    return (
      <div style={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: '4px',
        marginTop: '6px'
      }}>
        {products.map((product, idx) => (
          <Tag key={idx} color="blue" style={{ margin: 0, fontSize: '11px' }}>
            {product}
          </Tag>
        ))}
      </div>
    )
  }

  const dispatchProductCodeQty = (dispatchArray: any) => {
    const array: any = []
    dispatchArray.dispatchingDetails.forEach((detail: any) => {
      const productDetail: any = {}
      productDetail.productCode = detail.box?.product?.productCode
      productDetail.quantity = detail.quantity
      array.push(productDetail)
    })

    const productArray = Array.from(
      new Set(array.map((item: any) => item.productCode))
    ).map((item: any) => {
      return { productCode: item, quantity: 0 }
    })
    productArray.forEach((item: any) => {
      array.forEach((i: any) => {
        if (item.productCode === i.productCode) {
          item.quantity += i.quantity
        }
      })
    })
    let productString = ''
    productArray.forEach((item: any) => {
      productString += item.productCode + ' ×' + item.quantity
      productString +=
        productArray.indexOf(item) !== productArray.length - 1 ? ', ' : ''
    })
    return productString
  }

  const getStatusColor = (statusName: string) => {
    const lower = statusName?.toLowerCase() || ''
    if (lower.includes('delivered')) return 'green'
    if (lower.includes('cancelled')) return 'error'
    if (lower.includes('picking') || lower.includes('to be delivered')|| lower.includes('on board')) return 'blue'
    return 'default'
  }

  const sectionStyle = {
    marginBottom: '12px'
  }

  const sectionTitleStyle = {
    fontSize: '13px',
    fontWeight: 600,
    color: '#262626',
    marginBottom: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  }

  const emptyTextStyle = {
    color: '#bfbfbf',
    fontStyle: 'italic',
    fontSize: '12px',
    padding: '4px 0'
  }

  const content = (
    <div style={{ width: '440px', maxHeight: '520px', overflowY: 'auto' }}>
      {loading ? (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          padding: '40px 0',
          flexDirection: 'column',
          gap: '12px'
        }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 28 }} spin />} />
          <span style={{ color: '#8c8c8c', fontSize: '12px' }}>Loading...</span>
        </div>
      ) : (
        <>
          {/* Packing Slip Section */}
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>
              <span>📦</span>
              <span>Packing Slip</span>
            </div>
            {getDispatchingData[0]?.dispatchId ? (
              getDispatchingData.map((item: any, index: number) => {
                const productString = dispatchProductCodeQty(item)
                const isVoided = !productString
                const statusName = item.statusNavigation.statusName
                const deliveryInfo = item?.courier?.courierName
                const deliveryId = item?.courier?.courierId
                const deliveredAt = item.deliveredAt 
                  ? moment.utc(item.deliveredAt).local().format('DD/MM/YYYY HH:mm')
                  : null
                
                return (
                  <div 
                    key={index}
                    style={{ 
                      marginBottom: '8px',
                      padding: '8px',
                      backgroundColor: isVoided ? '#fafafa' : '#fff',
                      borderRadius: '4px',
                      border: '1px solid #e8e8e8',
                      opacity: isVoided ? 0.6 : 1
                    }} 
                  >
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '6px'
                    }}>
                      <span style={{ fontWeight: 600, fontSize: '12px' }}>
                        {item.packingSlipNo}
                      </span>
                      <span style={{ color: '#8c8c8c', fontSize: '11px' }}>
                        {moment.utc(item.createdAt).local().format('DD/MM/YYYY')}
                      </span>
                    </div>

                    {!isVoided && (
                      <div style={{ 
                        display: 'flex',
                        gap: '4px',
                        marginBottom: '6px',
                        flexWrap: 'wrap'
                      }}>
                        <Tag color={getStatusColor(statusName)} style={{ margin: 0, fontSize: '11px' }}>
                          {statusName}
                        </Tag>
                        {deliveryInfo && (
                          <Tag style={{ margin: 0, fontSize: '11px' }}>
                            {deliveryInfo?.toLowerCase().includes('jadcup delivery') ||
                            deliveryInfo?.toLowerCase().includes('customer pickup') ||
                            deliveryInfo?.toLowerCase().includes('internal use')
                              ? 'Delivered by'
                              : 'Dispatched by'} {deliveryInfo}
                          </Tag>
                        )}
                      </div>
                    )}

                    {deliveredAt && !isVoided && (
                      <div style={{ fontSize: '11px', color: '#52c41a', marginBottom: '4px' }}>
                        ✓ {deliveredAt}
                      </div>
                    )}

                    {item.trackingNo && !isVoided && (
                      <div style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: '4px' }}>
                        📍 {item.trackingNo}
                      </div>
                    )}

                    {productString && renderPackingSlipProducts(productString)}
                    
                    {isVoided && (
                      <Tag color="error" style={{ margin: 0, fontSize: '11px' }}>VOIDED</Tag>
                    )}
                  </div>
                )
              })
            ) : (
              <div style={emptyTextStyle}>No data</div>
            )}
          </div>

          <Divider style={{ margin: '8px 0' }} />

          {/* Invoice Section */}
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>
              <span>💰</span>
              <span>Invoice</span>
            </div>
            {getInvoiceData[0]?.invoiceId ? (
              getInvoiceData.map((item: any) => {
                const isRefund = Math.sign(item.totalPrice) < 0
                const invoiceDate = item?.invoiceDate 
                  ? moment.utc(item.invoiceDate).local().format('DD/MM/YYYY')
                  : ''
                
                return (
                  <div 
                    key={item.invoiceId} 
                    style={{ 
                      marginBottom: '8px',
                      padding: '8px',
                      backgroundColor: isRefund ? '#fff7e6' : '#f6ffed',
                      borderRadius: '4px',
                      border: isRefund ? '1px solid #ffd591' : '1px solid #b7eb8f'
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '4px'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ 
                          fontWeight: 600, 
                          color: isRefund ? '#d48806' : '#389e0d',
                          fontSize: '12px',
                          marginBottom: '2px'
                        }}>
                          {isRefund ? '↩️ Refund' : '📄 Invoice'}
                        </div>
                        {item?.invoiceNote && (
                          <div style={{ color: '#8c8c8c', fontSize: '11px', fontStyle: 'italic' }}>
                            {item.invoiceNote}
                          </div>
                        )}
                      </div>
                      <span style={{ 
                        fontWeight: 700,
                        color: isRefund ? '#d48806' : '#389e0d',
                        fontSize: '14px',
                        marginLeft: '8px'
                      }}>
                        ${Math.abs(item.totalPrice).toFixed(2)}
                      </span>
                    </div>
                    
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      {invoiceDate && (
                        <span style={{ color: '#8c8c8c', fontSize: '11px' }}>
                          🗓️ {invoiceDate}
                        </span>
                      )}
                      {!isRefund && (
                        <Tag color={item?.paid === 1 ? 'success' : 'error'} style={{ margin: 0, fontSize: '10px' }}>
                          {item?.paid === 1 ? '✓ Paid' : '✗ Not Paid'}
                        </Tag>
                      )}
                    </div>
                  </div>
                )
              })
            ) : (
              <div style={emptyTextStyle}>No data</div>
            )}
          </div>

          <Divider style={{ margin: '8px 0' }} />

          {/* Delivery Address Section */}
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>
              <span>📍</span>
              <span>Delivery Address</span>
            </div>
            <div style={{ 
              padding: '8px',
              backgroundColor: '#fafafa',
              borderRadius: '4px',
              color: '#595959', 
              lineHeight: '1.5', 
              fontSize: '12px' 
            }}>
              {getOrderData.deliveryAddress || <span style={emptyTextStyle}>No address</span>}
            </div>
          </div>

          <Divider style={{ margin: '8px 0' }} />

          {/* Contact Info Section */}
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>
              <span>📧</span>
              <span>Contact Info</span>
            </div>
            <div style={{ fontSize: '12px', lineHeight: '1.5' }}>
              {getOrderData?.customer?.email ? (
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#8c8c8c' }}>Email: </span>
                  <a
                    href={`mailto:${getOrderData.customer.email}`}
                    style={{
                      color: '#1890ff',
                      textDecoration: 'none',
                      fontWeight: 500
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                  >
                    {getOrderData.customer.email}
                  </a>
                </div>
              ) : null}

              {!getOrderData?.customer?.email && (
                <div style={emptyTextStyle}>No contact info</div>
              )}
            </div>
          </div>

          <Divider style={{ margin: '8px 0' }} />

          {/* Order Operators Section */}
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>
              <span>👤</span>
              <span>Operators</span>
            </div>
            {/* Created / Updated / Approved 横向排列，避免弹窗被撑得过高 */}
            <div style={{ fontSize: '12px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: '12px', rowGap: '4px' }}>
              {getOrderData?.createdEmployee?.firstName && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ color: '#8c8c8c' }}>Created: </span>
                  <span style={{ color: '#262626', fontWeight: 500 }}>
                    {getOrderData.createdEmployee.firstName}
                  </span>
                </div>
              )}
              {getOrderData?.operEmployee?.firstName && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ color: '#8c8c8c' }}>Updated: </span>
                  <span style={{ color: '#262626', fontWeight: 500 }}>
                    {getOrderData.operEmployee.firstName}
                  </span>
                </div>
              )}
              {(() => {
                // 审批人：GetOrderDto.ApprovalEmployee（GetEmployeeDto2）
                const approver = getOrderData?.approvalEmployee || null
                const approverName = approver
                   ? [ approver.firstName].filter(Boolean).join(' ')
                  // ? [approver.firstName, approver.lastName].filter(Boolean).join(' ')
                  : ''
                if (!approverName) return null
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ color: '#8c8c8c' }}>Approved: </span>
                    <span style={{ color: '#262626', fontWeight: 500 }}>
                      {approverName}
                    </span>
                  </div>
                )
              })()}
              {!getOrderData?.createdEmployee?.firstName && !getOrderData?.operEmployee?.firstName && (
                <div style={emptyTextStyle}>No data</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )

  return (
    <Popover
      content={content}
      placement='right'
      title={
        <div style={{ 
          textAlign: 'center', 
          fontWeight: 600, 
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px'
        }}>
          <span>📋</span>
          <span>Order Details</span>
        </div>
      }
      trigger='hover'
      visible={visible}
      onVisibleChange={handleVisibleChange}
    >
      <Button type='primary' ghost>
        Details
      </Button>
    </Popover>
  )
}

export default ItipsForOrderDetails
